import { add, max, min, sub, Duration } from 'date-fns'

export interface ClampDateProps {
  past?: Duration
  future?: Duration
}

/**
 * Clamps a given date to a specified range in the past or future.
 *
 * @param date - The date to be clamped. Can be a string, number, or Date object.
 * @param clamp - An object specifying the clamping range. It contains:
 *   - `future`: A duration (e.g., { days: 7 }) to clamp the date to the future.
 *   - `past`: A duration (e.g., { days: 30 }) to clamp the date to the past.
 *
 * @returns The clamped date as an ISO string.
 *
 * @throws Will log an error to the console if the clamping process fails and return
 *  the original date as an ISO string.
 *
 * @example
 * ```typescript
 * const clampedDate = clampDate('2023-01-01', { future: { days: 7 }, past: { days: 30 } });
 * console.log(clampedDate); // Outputs a clamped ISO date string
 * ```
 */
export const clampDate = (date: string | number | Date, clamp: ClampDateProps): string => {
  try {
    const baseDate = new Date(date)
    if (isNaN(baseDate.getTime())) throw new RangeError('Invalid input date')

    const clampedDate = Object.entries(clamp).reduce(
      (accDate, [key, value]) =>
        key === 'future' && value
          ? min([new Date(accDate), add(new Date(), value)])
          : key === 'past' && value
          ? max([new Date(accDate), sub(new Date(), value)])
          : accDate,
      baseDate,
    )
    return new Date(clampedDate).toISOString()
  } catch (error) {
    console.error('Error clamping date:', error)
    try {
      return new Date(date).toISOString()
    } catch {
      return new Date().toISOString()
    }
  }
}
